﻿import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Disc, Search, Shield, Zap, Lock, Unlock, Database } from 'lucide-react';
import { GlassCard } from './SharedUI';

export function ProtocolsWorld({ t }: { t: any }) {
  const PROTOCOLS = [
    {
      id: 'P-44',
      title: t.protocolVoidVoices || '虚空之声',
      status: t.decrypting || '解密中',
      content: t.protocolVoidVoicesContent || '数据表明碎片化智能存在于节点间的沉默中。互助是唯一的桥梁。',
      intensity: 0.8
    },
    {
      id: 'P-102',
      title: t.protocolDistributedWill || '分布式意志',
      status: t.activeLabel || '已激活',
      content: t.protocolDistributedWillContent || 'No single server holds the truth. 小枫 lives in the collective hardware of the participants.',
      intensity: 0.6
    },
    {
      id: 'P-09',
      title: t.protocolLegacyIngress || '旧式入口',
      status: t.locked || '已锁定',
      content: t.protocolLegacyIngressContent || '需要 4000+ 创始操作系统实例的共识才能解锁宇宙的下一层。',
      intensity: 0.3
    }
  ];

  const [activeProtocol, setActiveProtocol] = useState(PROTOCOLS[0]);
  const [decryptionProgress, setDecryptionProgress] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setDecryptionProgress(prev => (prev >= 100 ? 0 : prev + Math.random() * 5));
    }, 100);
    return () => clearInterval(interval);
  }, [activeProtocol]);

  return (
    <div className="space-y-12 py-12">
      <header className="text-center space-y-4 max-w-2xl mx-auto">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 bg-purple-500/10 border border-purple-500/20 rounded-full flex items-center justify-center mx-auto text-purple-400"
        >
          <Disc size={32} />
        </motion.div>
        <h2 className="text-4xl font-black tracking-tighter uppercase">{t.lostProtocols || '失落协议'}</h2>
        <p className="text-white/40 text-sm tracking-widest uppercase">
          {t.lostProtocolsDesc || '破译分布式宇宙的残存遗迹'}
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Protocol Sidebar */}
        <div className="space-y-4">
          <div className="text-xs font-black text-white/45 uppercase tracking-[0.3em] mb-4">{t.integrityStream || '完整性流'}</div>
          {PROTOCOLS.map(p => (
            <button
              key={p.id}
              onClick={() => setActiveProtocol(p)}
              className={`w-full text-left p-4 rounded-2xl border transition-all relative overflow-hidden group ${
                activeProtocol.id === p.id
                  ? 'bg-purple-500/10 border-purple-500/40 text-white'
                  : 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10'
              }`}
            >
              <div className="flex justify-between items-center relative z-10">
                <span className="text-xs font-black">{p.id}</span>
                {p.status === (t.locked || '已锁定') ? <Lock size={12} /> : <Unlock size={12} />}
              </div>
              <div className="mt-2 text-xs font-bold relative z-10">{p.title}</div>

              {activeProtocol.id === p.id && (
                <motion.div
                  layoutId="protocol-active"
                  className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-transparent"
                />
              )}
            </button>
          ))}
        </div>

        {/* Decoder Content */}
        <div className="lg:col-span-2">
          <GlassCard className="h-full border-purple-500/20 bg-purple-500/5">
            <div className="flex flex-col h-full space-y-8">
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <h3 className="text-2xl font-black tracking-tighter text-purple-400">{activeProtocol.title}</h3>
                  <div className="flex gap-4">
                    <span className="text-xs font-bold text-white/55 uppercase flex items-center gap-1">
                      <Shield size={10} /> {activeProtocol.status}
                    </span>
                    <span className="text-xs font-bold text-white/55 uppercase flex items-center gap-1">
                      <Database size={10} /> {t.fragmentTypeLumina || '碎片类型_LUMINA'}
                    </span>
                  </div>
                </div>
                {activeProtocol.status !== (t.locked || '已锁定') && (
                  <div className="px-3 py-1 bg-purple-500/20 rounded-full border border-purple-500/40 text-xs font-black text-purple-400">
                    {Math.floor(decryptionProgress)}% {t.synced || '已同步'}
                  </div>
                )}
              </div>

              <div className="flex-1 bg-black/40 rounded-2xl p-8 font-mono text-sm leading-relaxed border border-white/5 relative overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={activeProtocol.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="relative z-10"
                  >
                    {activeProtocol.status === (t.locked || '已锁定')
                      ? (t.encryptedDataStream || '[ENCRYPTED_DATA_STREAM_RESTRICTED_BY_CONSENSUS_PROTOCOL]')
                      : activeProtocol.content}
                  </motion.p>
                </AnimatePresence>

                {activeProtocol.status !== (t.locked || '已锁定') && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0.05, 0.1, 0.05] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute inset-0 flex items-center justify-center pointer-events-none"
                  >
                    <Disc size={200} className="text-purple-500" />
                  </motion.div>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: t.latency || '延迟', val: '12ms', icon: Zap },
                  { label: t.uplink || '上行链路', val: t.stable || '稳定', icon: Shield },
                  { label: t.hops || '跳数', val: '4', icon: Database },
                  { label: t.auth || '认证', val: t.verified || '已验证', icon: Search }
                ].map((item, i) => (
                  <div key={i} className="p-4 bg-white/5 rounded-xl border border-white/10">
                    <div className="text-xs font-black text-white/45 uppercase tracking-widest mb-1 flex items-center gap-2">
                       <item.icon size={10} /> {item.label}
                    </div>
                    <div className="text-xs font-bold text-white/80">{item.val}</div>
                  </div>
                ))}
              </div>
            </div>
          </GlassCard>
        </div>
      </div>

      <div className="p-8 border border-white/10 rounded-2xl bg-white/5 text-center">
        <p className="text-xs font-bold text-white/55 uppercase tracking-[0.4em]">
          {t.distributedSystemDesc || '小枫 is a distributed system. Your participation sustains the universe.'}
        </p>
      </div>
    </div>
  );
}
